import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const businessTypes = [
  { value: 'retail', label: 'Retail / Storefront', risk: 'low' },
  { value: 'office', label: 'Office / Professional Services', risk: 'low' },
  { value: 'restaurant', label: 'Restaurant / Food Service', risk: 'medium' },
  { value: 'contractor', label: 'Contractor / Construction', risk: 'high' },
  { value: 'trucking', label: 'Trucking / Transportation', risk: 'high' },
  { value: 'mining', label: 'Coal Mining / Extraction', risk: 'high' },
  { value: 'logging', label: 'Logging / Forestry', risk: 'high' },
  { value: 'manufacturing', label: 'Manufacturing / Industrial', risk: 'medium' },
  { value: 'other', label: 'Other Business', risk: 'medium' },
];

const revenueRanges = [
  { value: 'under250k', label: 'Under $250,000', multiplier: 1 },
  { value: '250k-1m', label: '$250,000 – $1M', multiplier: 1.5 },
  { value: '1m-5m', label: '$1M – $5M', multiplier: 2 },
  { value: '5m-20m', label: '$5M – $20M', multiplier: 2.5 },
  { value: 'over20m', label: 'Over $20M', multiplier: 3 },
];

const employeeRanges = [
  { value: '0', label: 'Just me (sole proprietor)', count: 0 },
  { value: '1-4', label: '1 – 4 employees', count: 2 },
  { value: '5-19', label: '5 – 19 employees', count: 10 },
  { value: '20-49', label: '20 – 49 employees', count: 30 },
  { value: '50plus', label: '50+ employees', count: 60 },
];

const propertyValues = [
  { value: 'none', label: 'No significant property / equipment', tiv: 0 },
  { value: 'under100k', label: 'Under $100,000', tiv: 100000 },
  { value: '100k-500k', label: '$100,000 – $500,000', tiv: 350000 },
  { value: '500k-2m', label: '$500,000 – $2M', tiv: 1250000 },
  { value: 'over2m', label: 'Over $2M', tiv: 2500000 },
];

interface Estimate {
  glMin: string;
  glMax: string;
  glNote: string;
  propertyNote: string;
  umbrellaRec: boolean;
  workersCompNote: string;
  autoNote: string;
  riskLevel: 'low' | 'medium' | 'high';
  gaps: string[];
}

function calcEstimate(
  bizType: string,
  revenue: string,
  employees: string,
  property: string,
  hasVehicles: boolean
): Estimate {
  const biz = businessTypes.find(b => b.value === bizType);
  const rev = revenueRanges.find(r => r.value === revenue);
  const emp = employeeRanges.find(e => e.value === employees);
  const prop = propertyValues.find(p => p.value === property);

  const risk = biz?.risk ?? 'medium';
  const revMult = rev?.multiplier ?? 1;
  const empCount = emp?.count ?? 0;
  const tiv = prop?.tiv ?? 0;

  let glMin = '$1M / $2M';
  let glMax = '$1M / $2M';
  let glNote = 'Standard limits for low-risk operations.';
  let umbrellaRec = false;

  if (risk === 'low') {
    if (revMult >= 2) {
      glMin = '$1M / $2M';
      glMax = '$2M / $4M';
      glNote = 'Mid-size low-risk operation. Consider higher limits as revenue grows.';
    } else {
      glMin = '$1M / $2M';
      glMax = '$1M / $2M';
      glNote = 'Standard limits are appropriate for your risk profile.';
    }
  } else if (risk === 'medium') {
    glMin = '$1M / $2M';
    glMax = '$2M / $4M';
    glNote = 'Mid-range limits recommended. Review contract requirements for higher minimums.';
    if (revMult >= 2) umbrellaRec = true;
  } else {
    glMin = '$2M / $4M';
    glMax = '$5M+';
    glNote = 'High-risk industry — higher limits are strongly recommended. Umbrella coverage is advised.';
    umbrellaRec = true;
  }

  let propertyNote = '';
  if (tiv === 0) {
    propertyNote = 'No significant property exposure identified. If you acquire equipment or a building, revisit this.';
  } else if (tiv <= 100000) {
    propertyNote = `Insure at full replacement value — estimated TIV up to $100,000. Don\'t underinsure to save premium.`;
  } else if (tiv <= 500000) {
    propertyNote = `Estimated TIV in the $100K–$500K range. Make sure your equipment schedule is accurate and up to date.`;
  } else if (tiv <= 2000000) {
    propertyNote = `Significant property exposure — TIV in the $500K–$2M range. Consider blanket property coverage and inland marine for off-site equipment.`;
  } else {
    propertyNote = `High TIV over $2M. Work with a specialist to structure proper blanket limits and scheduled coverage. Inland marine and builders risk may also apply.`;
  }

  let workersCompNote = '';
  if (empCount === 0) {
    workersCompNote = 'As a sole proprietor, workers comp may be optional — but check your state\'s rules. KY requires coverage for 1+ employees; VA for 2+; TN for 5+ (1+ in construction).';
  } else if (empCount <= 4) {
    workersCompNote = 'With 1–4 employees, workers comp is required in Kentucky and likely required in Virginia. In Tennessee, required for construction employers. Verify your state threshold.';
  } else {
    workersCompNote = 'With 5+ employees, workers comp is required in all three Tri-State states. Make sure all employees — including part-time and seasonal — are properly classified.';
  }

  const autoNote = hasVehicles
    ? 'Commercial auto coverage is required for all business-use vehicles. Personal auto will not cover work-related accidents. List every vehicle and driver.'
    : 'No vehicles indicated. If you ever use a personal vehicle for business purposes, consider hired/non-owned auto coverage.';

  const gaps: string[] = [];
  if (umbrellaRec) gaps.push('Umbrella / Excess Liability — extra limits above your GL and auto');
  if (risk === 'high') gaps.push('Pollution Liability — standard GL excludes most pollution claims');
  if (bizType === 'contractor' || bizType === 'mining' || bizType === 'logging') {
    gaps.push('Inland Marine — covers tools and equipment off-premises or in transit');
  }
  if (bizType === 'contractor') gaps.push('Builders Risk — covers projects under construction');
  if (bizType === 'trucking' || bizType === 'mining') gaps.push('Cargo / Motor Truck Cargo — covers freight and materials in transit');

  return { glMin, glMax, glNote, propertyNote, umbrellaRec, workersCompNote, autoNote, riskLevel: risk as 'low' | 'medium' | 'high', gaps };
}

const riskColors = {
  low: { badge: 'bg-emerald-900/40 text-emerald-400 border-emerald-500/30', label: 'Lower Risk' },
  medium: { badge: 'bg-amber-900/40 text-amber-400 border-amber-500/30', label: 'Moderate Risk' },
  high: { badge: 'bg-red-900/40 text-red-400 border-red-500/30', label: 'Higher Risk' },
};

export default function CoverageEstimator() {
  const [bizType, setBizType] = useState('');
  const [revenue, setRevenue] = useState('');
  const [employees, setEmployees] = useState('');
  const [property, setProperty] = useState('');
  const [hasVehicles, setHasVehicles] = useState<boolean | null>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    document.title = 'Business Insurance Coverage Estimator | Summit Appalachia Insurance';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'A free tool to estimate how much general liability, property, workers comp, and commercial auto coverage your Appalachian business needs. Answer 5 questions to get started.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/coverage-estimator`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/coverage-estimator#tool`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/coverage-estimator`,
      name: 'Business Insurance Coverage Estimator',
      description: 'A free tool to estimate how much general liability, property, workers comp, and commercial auto coverage your Appalachian business needs.',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-coverage-estimator';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-coverage-estimator')?.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  const allAnswered = bizType && revenue && employees && property && hasVehicles !== null;

  const estimate = allAnswered
    ? calcEstimate(bizType, revenue, employees, property, hasVehicles as boolean)
    : null;

  const handleCalculate = () => {
    if (allAnswered) setShowResults(true);
  };

  const handleReset = () => {
    setBizType('');
    setRevenue('');
    setEmployees('');
    setProperty('');
    setHasVehicles(null);
    setShowResults(false);
  };

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/79a95dace0a7c1a078f6cc8d7fcd2138.jpg"
            alt="Coverage Estimator"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Tool
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Tool</span>
            <span className="text-white/30 text-xs font-sans">3 min · Jan 2026</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Coverage<br />
            <span className="text-[#D4AF37]">Estimator</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            Answer five quick questions and get a ballpark idea of the right coverage limits for your business — general liability, property, workers comp, and more.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              Not sure how much coverage your business actually needs? You're not alone. This estimator gives you a quick, ballpark idea of the right coverage limits based on your business size, revenue, and risk level. <strong className="text-white">It's not a quote — but it's a smart starting point.</strong>
            </p>
          </div>

          {/* What this helps with */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-14">
            {[
              { icon: 'ri-shield-line', text: 'Estimating General Liability limits' },
              { icon: 'ri-building-2-line', text: 'Determining Property coverage (TIV)' },
              { icon: 'ri-search-eye-line', text: 'Identifying potential coverage gaps' },
              { icon: 'ri-scales-3-line', text: 'Avoiding being underinsured or overpaying' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 bg-[#111111] border border-[#D4AF37]/15 rounded-md px-5 py-4">
                <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                  <i className={`${item.icon} text-lg`} />
                </div>
                <span className="text-white/65 text-sm font-sans">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-4 pb-20 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
              <i className="ri-calculator-line text-xl" />
            </div>
            <h2 className="font-serif text-3xl text-white">The Estimator</h2>
          </div>

          {!showResults ? (
            <div className="space-y-8">
              {/* Q1 */}
              <div>
                <p className="text-white text-sm font-bold font-sans uppercase tracking-widest mb-4">
                  <span className="text-[#D4AF37] mr-2">01</span> What type of business do you operate?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {businessTypes.map(b => (
                    <button
                      key={b.value}
                      onClick={() => setBizType(b.value)}
                      className={`px-4 py-3 rounded-md text-sm font-sans text-left transition-all cursor-pointer whitespace-nowrap border ${
                        bizType === b.value
                          ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-[#111111] border-[#D4AF37]/15 text-white/50 hover:border-[#D4AF37]/40 hover:text-white/80'
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q2 */}
              <div>
                <p className="text-white text-sm font-bold font-sans uppercase tracking-widest mb-4">
                  <span className="text-[#D4AF37] mr-2">02</span> What is your annual revenue?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {revenueRanges.map(r => (
                    <button
                      key={r.value}
                      onClick={() => setRevenue(r.value)}
                      className={`px-4 py-3 rounded-md text-sm font-sans text-left transition-all cursor-pointer whitespace-nowrap border ${
                        revenue === r.value
                          ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-[#111111] border-[#D4AF37]/15 text-white/50 hover:border-[#D4AF37]/40 hover:text-white/80'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q3 */}
              <div>
                <p className="text-white text-sm font-bold font-sans uppercase tracking-widest mb-4">
                  <span className="text-[#D4AF37] mr-2">03</span> How many employees do you have?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {employeeRanges.map(e => (
                    <button
                      key={e.value}
                      onClick={() => setEmployees(e.value)}
                      className={`px-4 py-3 rounded-md text-sm font-sans text-left transition-all cursor-pointer whitespace-nowrap border ${
                        employees === e.value
                          ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-[#111111] border-[#D4AF37]/15 text-white/50 hover:border-[#D4AF37]/40 hover:text-white/80'
                      }`}
                    >
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q4 */}
              <div>
                <p className="text-white text-sm font-bold font-sans uppercase tracking-widest mb-4">
                  <span className="text-[#D4AF37] mr-2">04</span> What is the estimated value of your equipment / property?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {propertyValues.map(p => (
                    <button
                      key={p.value}
                      onClick={() => setProperty(p.value)}
                      className={`px-4 py-3 rounded-md text-sm font-sans text-left transition-all cursor-pointer border ${
                        property === p.value
                          ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-[#111111] border-[#D4AF37]/15 text-white/50 hover:border-[#D4AF37]/40 hover:text-white/80'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q5 */}
              <div>
                <p className="text-white text-sm font-bold font-sans uppercase tracking-widest mb-4">
                  <span className="text-[#D4AF37] mr-2">05</span> Do you use vehicles for business?
                </p>
                <div className="flex gap-3">
                  {[{ val: true, label: 'Yes — work trucks, vans, or fleet vehicles' }, { val: false, label: 'No — no business vehicles' }].map(opt => (
                    <button
                      key={String(opt.val)}
                      onClick={() => setHasVehicles(opt.val)}
                      className={`px-5 py-3 rounded-md text-sm font-sans transition-all cursor-pointer whitespace-nowrap border ${
                        hasVehicles === opt.val
                          ? 'bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-[#111111] border-[#D4AF37]/15 text-white/50 hover:border-[#D4AF37]/40 hover:text-white/80'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <button
                  onClick={handleCalculate}
                  disabled={!allAnswered}
                  className={`px-10 py-4 rounded-md text-xs font-bold uppercase tracking-widest font-sans transition-all cursor-pointer whitespace-nowrap ${
                    allAnswered
                      ? 'bg-[#D4AF37] text-black hover:bg-[#e8c84e]'
                      : 'bg-[#1a1a1a] text-white/20 border border-white/10 cursor-not-allowed'
                  }`}
                >
                  {allAnswered ? 'Get My Estimate' : 'Answer All Questions to Continue'}
                </button>
              </div>
            </div>
          ) : (
            estimate && (
              <div className="space-y-6">
                {/* Risk level banner */}
                <div className={`flex items-center gap-4 border rounded-md px-6 py-4 ${riskColors[estimate.riskLevel].badge}`}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bar-chart-2-line text-xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest font-sans mb-0.5">Risk Profile</p>
                    <p className="text-sm font-sans">{riskColors[estimate.riskLevel].label} — based on your business type, size, and operations</p>
                  </div>
                </div>

                {/* GL */}
                <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#D4AF37]/10 flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37]">
                      <i className="ri-shield-check-line text-base" />
                    </div>
                    <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">General Liability</h3>
                  </div>
                  <div className="px-6 py-5">
                    <div className="flex flex-wrap items-baseline gap-3 mb-3">
                      <span className="font-serif text-2xl text-[#D4AF37]">{estimate.glMin}</span>
                      {estimate.glMax !== estimate.glMin && (
                        <>
                          <span className="text-white/30 text-sm font-sans">to</span>
                          <span className="font-serif text-2xl text-[#D4AF37]">{estimate.glMax}</span>
                        </>
                      )}
                      <span className="text-white/30 text-xs font-sans uppercase tracking-wider">Recommended Range</span>
                    </div>
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{estimate.glNote}</p>
                  </div>
                </div>

                {/* Property */}
                <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#D4AF37]/10 flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37]">
                      <i className="ri-building-2-line text-base" />
                    </div>
                    <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">Property Coverage (TIV)</h3>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{estimate.propertyNote}</p>
                  </div>
                </div>

                {/* Workers Comp */}
                <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#D4AF37]/10 flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37]">
                      <i className="ri-first-aid-kit-line text-base" />
                    </div>
                    <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">Workers Compensation</h3>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{estimate.workersCompNote}</p>
                  </div>
                </div>

                {/* Auto */}
                <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#D4AF37]/10 flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37]">
                      <i className="ri-truck-line text-base" />
                    </div>
                    <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">Commercial Auto</h3>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{estimate.autoNote}</p>
                  </div>
                </div>

                {/* Gaps */}
                {estimate.gaps.length > 0 && (
                  <div className="bg-[#111111] border border-amber-500/25 rounded-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-amber-500/15 flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center text-amber-400">
                        <i className="ri-error-warning-line text-base" />
                      </div>
                      <h3 className="font-sans text-xs font-bold text-amber-400 uppercase tracking-widest">Potential Coverage Gaps to Review</h3>
                    </div>
                    <ul className="px-6 py-5 space-y-3">
                      {estimate.gaps.map((gap, i) => (
                        <li key={i} className="flex items-start gap-3 text-white/55 text-sm font-sans leading-relaxed">
                          <span className="text-amber-400 mt-0.5 flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-base" />
                          </span>
                          {gap}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="bg-[#111111] border border-white/8 rounded-md px-6 py-5 flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center text-white/30 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-base" />
                  </div>
                  <p className="text-white/35 text-xs font-sans leading-relaxed">
                    <strong className="text-white/50">Important Note:</strong> This tool provides estimates only. Every business is different, and final coverage should always be reviewed by a licensed insurance professional. These numbers are a starting point — not a policy.
                  </p>
                </div>

                {/* Reset */}
                <div className="pt-2">
                  <button
                    onClick={handleReset}
                    className="px-6 py-3 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap font-sans"
                  >
                    <i className="ri-refresh-line mr-2" />
                    Start Over
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </section>

      {/* GL / Property Reference */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Quick Reference Guide</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            General benchmarks used in this estimator — based on industry standards and common carrier guidelines.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* GL table */}
            <div className="bg-[#111111] border border-[#D4AF37]/15 rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-[#D4AF37]/10">
                <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">General Liability Estimate Guide</h3>
              </div>
              <div className="divide-y divide-white/5">
                {[
                  { label: 'Small / low-risk businesses', val: '$1M / $2M limits' },
                  { label: 'Mid-size operations', val: '$1M–$2M / $2M–$4M' },
                  { label: 'High-risk (contractors, trucking, mining)', val: '$2M–$5M+ with umbrella' },
                ].map((row, i) => (
                  <div key={i} className="px-5 py-3.5 flex items-start justify-between gap-4">
                    <span className="text-white/45 text-xs font-sans leading-relaxed">{row.label}</span>
                    <span className="text-[#D4AF37] text-xs font-bold font-sans whitespace-nowrap">{row.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Property table */}
            <div className="bg-[#111111] border border-[#D4AF37]/15 rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-[#D4AF37]/10">
                <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">Property Coverage (TIV) Includes</h3>
              </div>
              <div className="divide-y divide-white/5">
                {['Buildings', 'Equipment', 'Tools', 'Inventory'].map((item, i) => (
                  <div key={i} className="px-5 py-3.5 flex items-center gap-3">
                    <span className="text-[#D4AF37] flex-shrink-0">
                      <i className="ri-checkbox-circle-line text-sm" />
                    </span>
                    <span className="text-white/45 text-xs font-sans">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-[#111111] border border-[#D4AF37]/20 rounded-md px-6 py-5 flex items-start gap-4">
            <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-base" />
            </div>
            <p className="text-white/55 text-sm font-sans leading-relaxed">
              <strong className="text-white">Underinsuring property is one of the most common and costly mistakes.</strong> Your property coverage should equal the full replacement value — not the market value or what you paid for it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0a0a0a] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Want Exact Numbers?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            This tool gives you a starting point. For a customized quote built specifically for your business and industry, contact Summit Appalachia Insurance — we work with 50+ direct carriers and 120+ broker relationships to find the right fit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get an Accurate Quote
            </Link>
            <Link
              to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
